import { AnyAction, createSlice, PayloadAction, ThunkAction } from '@reduxjs/toolkit';

import type { RootState } from 'src/store';
import {
  Attendee,
  AttendeeActivity,
  AttendeeTicket,
  EmailActivityResponse,
  Order,
} from 'src/types/types';
import { Nullable } from 'primereact/ts-helpers';
import { FEATURE_NAME } from 'src/redesign/features/Attendees/store/constants';
import * as actions from './actions';

export type AttendeeSliceState = {
  isLoading: boolean;
  isInsightsLoading?: boolean;
  isAttendeesLoading: boolean;
  isAttendeesStatsLoading: boolean;
  isMoreAttendeesLoading: boolean;
  isOrderLoading: boolean;
  isAttendeeTicketsLoading: boolean;
  data: Attendee[] | null;
  attendeeWorkflows: any[];
  attendeeWaitlist: any[];
  attendeeMonthlyTransactions: any[];
  attendeeDailyTransactions: any[];
  attendeeStats: any;
  attendeeTickets: AttendeeTicket[] | null;
  current?: Attendee;
  order?: Nullable<Order>;
  error: Error | null | boolean;
  emailActivity: Nullable<EmailActivityResponse>;
  attendeeActivitiesLoading: boolean;
  attendeeActivities: Nullable<AttendeeActivity[]>;
  cartRequests: any[] | null;
  isCartRequestsLoading: boolean;
  hostPendingRequests: any[] | null;
  isHostPendingRequestsLoading: boolean;
};

const initialState: AttendeeSliceState = {
  attendeeDailyTransactions: [],
  attendeeWaitlist: [],
  isLoading: false,
  attendeeStats: null,
  isAttendeesLoading: true,
  isAttendeesStatsLoading: false,
  isMoreAttendeesLoading: false,
  isOrderLoading: false,
  isAttendeeTicketsLoading: false,
  data: null,
  attendeeWorkflows: [],
  attendeeMonthlyTransactions: [],
  attendeeTickets: null,
  error: null,
  order: null,
  emailActivity: null,
  attendeeActivitiesLoading: false,
  attendeeActivities: null,
  cartRequests: null,
  isCartRequestsLoading: false,
  hostPendingRequests: null,
  isHostPendingRequestsLoading: false,
};

export const attendeesSlice = createSlice({
  name: FEATURE_NAME,
  initialState,
  reducers: {
    attendees_attempt: (state: AttendeeSliceState, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload || false;
    },
    attendees_success: (state, action: PayloadAction<Attendee[]>) => {
      state.data = action.payload;
      state.isLoading = false;
    },
    attendees_error: (state, action: PayloadAction<Error>) => {
      state.error = action.payload;
      state.isLoading = false;
    },

    submit_attendee_attempt: (state) => {
      state.isLoading = true;
    },
    submit_attendee_success: (state, action: PayloadAction<Attendee>) => {
      state.data = state.data.concat(action.payload);
      state.isLoading = false;
    },
    submit_attendee_error: (state, action: PayloadAction<Error>) => {
      state.error = action.payload;
      state.isLoading = false;
    },
    update_attendee_attempt: (state) => {
      state.isLoading = true;
    },
    update_attendee_error: (state, action: PayloadAction<Error>) => {
      state.error = action.payload;
      state.isLoading = false;
    },
    single_attendee_success: (state, action: PayloadAction<Attendee>) => {
      state.current = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(actions.fetchAttendeesByEvent.pending, (state) => {
      state.isAttendeesLoading = true;
    });
    builder.addCase(actions.fetchAttendeesByEvent.fulfilled, (state, action) => {
      state.data = action.payload.data;
      state.isAttendeesLoading = false;
    });
    builder.addCase(actions.fetchAttendeesByEvent.rejected, (state) => {
      state.data = null;
      state.error = true;
      state.isAttendeesLoading = false;
    });

    builder.addCase(actions.fetchAttendeesByEventPagination.pending, (state, action) => {
      state.isMoreAttendeesLoading = true;
    });
    builder.addCase(actions.fetchAttendeesByEventPagination.fulfilled, (state, action) => {
      state.data = [...state.data, ...action.payload.data];
      state.isMoreAttendeesLoading = false;
    });
    builder.addCase(actions.searchAttendeesByEvent.pending, (state, action) => {
      const offset = action?.meta?.arg?.criteria?.offset || 0;
      if (offset === 0) {
        state.data = [];
        state.attendeeStats = {};
        state.isAttendeesLoading = true;
      }
    });
    builder.addCase(actions.searchAttendeesByEvent.fulfilled, (state, action) => {
      const pagination = action?.meta?.arg?.pagination;
      if (pagination) {
        state.data = state.data.concat(action.payload.data);
      } else {
        state.data = action.payload.data;
      }
      state.isAttendeesLoading = false;
    });

    builder.addCase(actions.searchAttendeesByEvent.rejected, (state) => {
      state.isAttendeesLoading = false;
      state.error = true;
    });
    builder.addCase(actions.searchAttendeesStatsByEvent.pending, (state) => {
      state.attendeeStats = null;
      state.isAttendeesStatsLoading = true;
    });
    builder.addCase(actions.searchAttendeesStatsByEvent.fulfilled, (state, action) => {
      state.isAttendeesStatsLoading = false;
      state.attendeeStats = action.payload.data;
    });
    builder.addCase(actions.searchAttendeesStatsByEvent.rejected, (state) => {
      state.isAttendeesStatsLoading = false;
      state.attendeeStats = null;
    });
    builder.addCase(actions.fetchEventWaitlistAttendees.fulfilled, (state, action) => {
      state.attendeeWaitlist = action.payload.data;
    });
    builder.addCase(actions.fetchWorkflowsByCustomerId.fulfilled, (state, action) => {
      state.attendeeWorkflows = action.payload.data;
    });
    builder.addCase(actions.fetchAttendeeMonthlyTransactions.fulfilled, (state, action) => {
      state.isInsightsLoading = false;
      state.attendeeMonthlyTransactions = action.payload.data;
    });
    builder.addCase(actions.fetchAttendeeMonthlyTransactions.pending, (state, action) => {
      state.isInsightsLoading = true;
    });
    builder.addCase(actions.fetchAttendeeDailyTransactions.pending, (state, action) => {
      state.isInsightsLoading = true;
    });
    builder.addCase(actions.fetchAttendeeDailyTransactions.fulfilled, (state, action) => {
      state.isInsightsLoading = false;
      state.attendeeDailyTransactions = action.payload.data;
    });
    builder.addCase(actions.fetchCustomers.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(actions.fetchCustomers.fulfilled, (state, action) => {
      state.data = action.payload.data;
      state.isLoading = false;
    });
    builder.addCase(actions.fetchCustomer.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(actions.fetchCustomer.fulfilled, (state, action) => {
      state.current = {
        ...action.payload.data,
      };
      state.isLoading = false;
    });
    builder.addCase(actions.fetchAttendee.pending, (state, action) => {
      state.current = null;
      state.isLoading = true;
    });
    builder.addCase(actions.fetchAttendee.fulfilled, (state, action) => {
      state.current = action.payload.data;
      state.isLoading = false;
    });
    builder.addCase(actions.fetchOrder.pending, (state, action) => {
      state.order = null;
      state.isOrderLoading = true;
    });
    builder.addCase(actions.fetchOrder.fulfilled, (state, action) => {
      state.order = action.payload.data;
      state.isOrderLoading = false;
    });
    builder.addCase(actions.fetchOrder.rejected, (state, action) => {
      state.isOrderLoading = false;
    });
    builder.addCase(actions.deleteAttendee.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(actions.deleteAttendee.fulfilled, (state, action) => {
      state.isLoading = false;
      // Remove the deleted attendee from the data array
      state.data = state.data.filter((data) => action.meta.arg !== data.id);

      // If the deleted attendee is the current attendee, clear it
      if (state.current && state.current.id === action.meta.arg) {
        state.current = null;
      }

      // Update attendee stats if they exist
      if (state.attendeeStats) {
        const stats = state.attendeeStats;
        if (stats.attendance_count) {
          stats.attendance_count = Math.max(0, stats.attendance_count - 1);
        }
      }
    });
    builder.addCase(actions.deleteAttendee.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error;
    });
    // builder.addCase(createAttendeeApi.fulfilled, (state, action) => {
    //     state.data = [action .payload.data.attendees].concat(state.data);
    // });
    builder.addCase(actions.fetchAttendeeByEmail.fulfilled, (state, action) => {
      state.data = action.payload.data;
    });
    builder.addCase(actions.updateAttendee.fulfilled, (state, action) => {
      // Update the attendee in the data array if it exists
      if (state.data && state.data.length > 0) {
        const updatedAttendeeIndex = state.data.findIndex(
          (attendee) => attendee.id === action.meta.arg.id,
        );

        if (updatedAttendeeIndex !== -1) {
          const customer = action.meta.arg.customer;
          state.data[updatedAttendeeIndex] = {
            ...state.data[updatedAttendeeIndex],
            first_name: customer.first_name || state.data[updatedAttendeeIndex].first_name,
            last_name: customer.last_name || state.data[updatedAttendeeIndex].last_name,
            email: customer.email || state.data[updatedAttendeeIndex].email,
          };
        }
      }

      // If the updated attendee is the current attendee, update that too
      if (state.current && state.current.id === action.meta.arg.id) {
        const customer = action.meta.arg.customer;
        state.current = {
          ...state.current,
          first_name: customer.first_name || state.current.first_name,
          last_name: customer.last_name || state.current.last_name,
          email: customer.email || state.current.email,
        };
      }
    });
    builder.addCase(actions.patchAttendee.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(actions.patchAttendee.fulfilled, (state, action) => {
      // Update the attendee in the data array if it exists
      if (state.data && state.data.length > 0) {
        const updatedAttendeeIndex = state.data.findIndex(
          (attendee) => attendee.id === action.payload.data.id,
        );

        if (updatedAttendeeIndex !== -1) {
          state.data[updatedAttendeeIndex] = {
            ...state.data[updatedAttendeeIndex],
            status: action?.payload?.data?.status,
          };
        }
      }

      // If the updated attendee is the current attendee, update that too
      if (state.current && state.current.id === action.payload.data.id) {
        state.current = {
          ...state.current,
          ...action.payload.data,
        };
      }

      state.isLoading = false;
    });
    builder.addCase(actions.patchAttendee.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error;
    });
    builder.addCase(actions.updateAttendeeTicket.fulfilled, (state, action) => {
      const attendeeIndex = state.order.attendees.findIndex(
        (attendee) => attendee.id === action.payload.data.id,
      );
      if (attendeeIndex !== -1) {
        state.order.attendees[attendeeIndex].ticket = action.payload.data.ticket;
      }
    });
    builder.addCase(actions.fetchEmailActivities.fulfilled, (state, action) => {
      state.emailActivity = action.payload.data;
    });
    builder.addCase(actions.fetchAttendeeActivities.fulfilled, (state, action) => {
      state.attendeeActivities = action.payload.data;
      state.attendeeActivitiesLoading = false;
    });
    builder.addCase(actions.fetchAttendeeActivities.rejected, (state) => {
      state.attendeeActivitiesLoading = false;
    });
    builder.addCase(actions.fetchAttendeeActivities.pending, (state) => {
      state.attendeeActivitiesLoading = true;
    });

    builder.addCase(actions.fetchAttendeeTickets.pending, (state) => {
      state.isAttendeeTicketsLoading = true;
    });
    builder.addCase(actions.fetchAttendeeTickets.fulfilled, (state, action) => {
      state.attendeeTickets = action.payload.data.items;
      state.isAttendeeTicketsLoading = false;
    });
    builder.addCase(actions.fetchAttendeeTickets.rejected, (state) => {
      state.isAttendeeTicketsLoading = false;
      state.error = true;
    });

    builder.addCase(actions.fetchTicketReceiptPdf.fulfilled, (state) => {
      // Nothing to update in state, just successfully opened the PDF
    });

    builder.addCase(actions.fetchTicketPdf.fulfilled, (state) => {
      // Nothing to update in state, just successfully opened the PDF
    });

    builder.addCase(actions.fetchCartRequests.pending, (state) => {
      state.isCartRequestsLoading = true;
    });
    builder.addCase(actions.fetchCartRequests.fulfilled, (state, action) => {
      state.cartRequests = action.payload.data;
      state.isCartRequestsLoading = false;
    });
    builder.addCase(actions.fetchCartRequests.rejected, (state) => {
      state.isCartRequestsLoading = false;
      state.error = true;
    });

    builder.addCase(actions.fetchHostPendingRequests.pending, (state) => {
      state.isHostPendingRequestsLoading = true;
    });
    builder.addCase(actions.fetchHostPendingRequests.fulfilled, (state, action) => {
      state.hostPendingRequests = action.payload.data;
      state.isHostPendingRequestsLoading = false;
    });
    builder.addCase(actions.fetchHostPendingRequests.rejected, (state) => {
      state.isHostPendingRequestsLoading = false;
      state.error = true;
    });
  },
});

export const {
  attendees_attempt,
  attendees_success,
  attendees_error,
  single_attendee_success,
  submit_attendee_success,
} = attendeesSlice.actions;

export const getAttendee =
  (id: string | number): ThunkAction<void, RootState, unknown, AnyAction> =>
  async (dispatch, getState) => {
    const state = getState();
    const attendees = state.attendees.data;
    const currentEvent = attendees.find((item) => item.id == id);
    if (!currentEvent) return null;
    dispatch(single_attendee_success(currentEvent));
  };

export const getAttendees =
  (): ThunkAction<void, RootState, unknown, AnyAction> => async (dispatch, getState) => {
    const state = getState();
    const attendees = state?.attendees?.data;
    dispatch(attendees_success(attendees));
  };

export const submitEvent =
  (data: Attendee): ThunkAction<void, RootState, unknown, AnyAction> =>
  (dispatch) => {
    dispatch(submit_attendee_success(data));
  };

export default attendeesSlice;
